import React, {useEffect, useState} from 'react';

import {useStore} from 'effector-react';
import {saveAs} from 'file-saver';
import {useTranslation} from 'react-i18next';
import {useParams} from 'react-router-dom';

import {exportBlockingAuditEvent, fetchBlockingAuditEvent} from 'api/blockingAudit';
import {ReactComponent as DownloadIcon} from 'assets/svg/download.svg';
import {AuditGeneralInformation} from 'components/common/AuditLog/GeneralInformation';
import {AuditMarkingCodesTable} from 'components/common/AuditLog/MarkingCodesTable';
import {
    DetailCard,
    DetailHeader,
    DetailHeaderActions,
    DetailId,
    DetailTitle,
    EmptyDetail,
    EventBadge,
    Field,
    FieldLabel,
    FieldsGrid,
    FieldValue,
    Section,
    SectionTitle,
} from 'components/common/AuditLog/styled';
import {BackTo} from 'components/common/BackTo';
import {Page} from 'components/common/Page';
import {formatLocalDateTime} from 'components/pages/RecallAudit/utils';
import {Button, ButtonVariant} from 'components/ui-kit/Button';
import {Icon} from 'components/ui-kit/Icon';
import {Spinner} from 'components/ui-kit/Spinner';
import {PERMISSION_ALIASES} from 'core/constants/permissionAliases';
import {RestError, showRestError} from 'core/errors';
import {ROUTE} from 'core/routes';
import {permissions$} from 'models/auth';
import {BlockingAuditEntry, BlockingAuditEventType} from 'types/blocking-audit';

type DetailField =
    | Exclude<keyof BlockingAuditEntry, 'failedMarkingCodes'>
    | 'unblockingOrganization';
const eventFields: Record<BlockingAuditEventType, DetailField[]> = {
    BLOCKING_DOCUMENT_CREATED: ['documentId', 'affectedCodesCount', 'reason', 'scopes'],
    BLOCKING_DOCUMENT_PROCESSING: ['documentId'],
    BLOCKING_DOCUMENT_COMPLETED: ['documentId', 'affectedCodesCount'],
    BLOCKING_DOCUMENT_REJECTED: ['documentId', 'rejectionReason', 'errorCode'],
    BLOCKING_CREATED: ['documentId', 'markingCode', 'reason', 'scopes', 'blockingId', 'createdAt'],
    BLOCKING_REMOVED: [
        'documentId',
        'markingCode',
        'reason',
        'scopes',
        'blockingId',
        'createdAt',
        'removedAt',
        'removedBy',
        'unblockingOrganization',
        'unblockingReason',
        'unblockingComment',
    ],
};

export const BlockingAuditDetailPage = () => {
    const {id} = useParams<{id: string}>();
    const {t} = useTranslation();
    const permissions = useStore(permissions$);
    const [entry, setEntry] = useState<BlockingAuditEntry | null>(null);
    const [pending, setPending] = useState(true);
    const [exportPending, setExportPending] = useState(false);
    useEffect(() => {
        let active = true;
        setEntry(null);
        setPending(true);
        fetchBlockingAuditEvent(id)
            .then((value) => {
                if (active) setEntry(value);
            })
            .catch((error) => {
                if (active) showRestError(error as RestError);
            })
            .finally(() => {
                if (active) setPending(false);
            });
        return () => {
            active = false;
        };
    }, [id]);
    const exportPdf = async () => {
        if (exportPending) return;
        setExportPending(true);
        try {
            saveAs(await exportBlockingAuditEvent(id), `blocking-audit-${id}.pdf`);
        } catch (error) {
            showRestError(error as RestError);
        } finally {
            setExportPending(false);
        }
    };
    const renderValue = (key: DetailField): React.ReactNode => {
        if (key === 'unblockingOrganization')
            return (
                [entry?.organizationName || entry?.organizationId, entry?.organizationTin]
                    .filter((value) => value != null && value !== '')
                    .join(' / ') || '—'
            );
        const value = entry?.[key];
        if (value == null || value === '') return '—';
        if (key === 'scopes')
            return (
                entry?.scopes
                    ?.map((scope) =>
                        t(`dictionaries.products.blockingScope.${scope}`, {defaultValue: scope}),
                    )
                    .join(', ') || '—'
            );
        const dictionaries: Partial<Record<DetailField, string>> = {
            reason: 'blockingReason',
            sourceFormation: 'blockingSourceFormation',
            unblockingReason: 'unblockingReason',
        };
        if (dictionaries[key])
            return t(`dictionaries.products.${dictionaries[key]}.${value}`, {
                defaultValue: String(value),
            });
        if (['createdAt', 'removedAt'].includes(key)) return formatLocalDateTime(String(value));
        return value;
    };
    return (
        <Page title={t('pages.recallAudit.detail.title')}>
            <BackTo to={ROUTE.BLOCKING_AUDIT}>{t('pages.recallAudit.detail.back')}</BackTo>
            {pending ? (
                <EmptyDetail>
                    <Spinner />
                </EmptyDetail>
            ) : !entry ? (
                <EmptyDetail>{t('pages.recallAudit.detail.notFound')}</EmptyDetail>
            ) : (
                <DetailCard>
                    <DetailHeader>
                        <div>
                            <DetailTitle>{t('pages.recallAudit.detail.title')}</DetailTitle>
                            <DetailId>ID: {entry.eventId}</DetailId>
                        </div>
                        <DetailHeaderActions>
                            <EventBadge>
                                {t(`pages.blockingAudit.events.${entry.eventType}`, {
                                    defaultValue: entry.eventType,
                                })}
                            </EventBadge>
                            {permissions.includes(PERMISSION_ALIASES.blockingAuditLogExport) && (
                                <Button
                                    size="sm"
                                    variant={ButtonVariant.Secondary}
                                    disabled={exportPending}
                                    isLoading={exportPending}
                                    onClick={exportPdf}
                                    icon={<Icon source={DownloadIcon} width={16} height={16} />}
                                >
                                    {t('pages.recallAudit.export.pdf')}
                                </Button>
                            )}
                        </DetailHeaderActions>
                    </DetailHeader>
                    <AuditGeneralInformation entry={entry}>
                        {entry.eventType !== 'BLOCKING_REMOVED' && (
                            <Field>
                                <FieldLabel>
                                    {t('pages.blockingAudit.fields.sourceFormation')}
                                </FieldLabel>
                                <FieldValue>{renderValue('sourceFormation')}</FieldValue>
                            </Field>
                        )}
                    </AuditGeneralInformation>
                    <Section>
                        <SectionTitle>{t('pages.blockingAudit.details')}</SectionTitle>
                        <FieldsGrid>
                            {(eventFields[entry.eventType] || [])
                                .filter(
                                    (key) =>
                                        key !== 'unblockingComment' || !!entry.unblockingComment,
                                )
                                .map((key) => (
                                    <Field key={key} wide={key === 'unblockingComment'}>
                                        <FieldLabel>
                                            {t(`pages.blockingAudit.fields.${key}`)}
                                        </FieldLabel>
                                        <FieldValue>{renderValue(key)}</FieldValue>
                                    </Field>
                                ))}
                        </FieldsGrid>
                    </Section>
                    {entry.eventType === 'BLOCKING_DOCUMENT_REJECTED' &&
                        !!entry.failedMarkingCodes?.length && (
                            <Section>
                                <SectionTitle>
                                    {t('pages.blockingAudit.failedMarkingCodes')}
                                </SectionTitle>
                                <AuditMarkingCodesTable
                                    key={entry.eventId}
                                    codes={entry.failedMarkingCodes}
                                    label={t('pages.blockingAudit.fields.markingCode')}
                                />
                            </Section>
                        )}
                </DetailCard>
            )}
        </Page>
    );
};
